import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getAuth, signOut } from "firebase/auth";
import {
  Bars3Icon,
  XMarkIcon,
  Cog6ToothIcon,
  NewspaperIcon,
  CalendarIcon,
  BookOpenIcon,
  FolderOpenIcon,
  UserGroupIcon,
  ExclamationCircleIcon,
  AcademicCapIcon,
  ArrowLeftOnRectangleIcon,
  BookmarkSquareIcon,
  EnvelopeIcon,
  EnvelopeOpenIcon,
  TrophyIcon,
} from "@heroicons/react/24/outline";

const DashboardLayout = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const auth = getAuth();

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  const closeMobileSidebar = () => setIsMobileOpen(false);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      navigate("/");
    } catch (err) {
      console.error("Logout failed:", err);
    }
  };

  const navItems = [
    { to: "site-config", label: "Site Configuration", icon: Cog6ToothIcon },
    { to: "news", label: "News Management", icon: NewspaperIcon },
    { to: "events", label: "Event Management", icon: CalendarIcon },
    { to: "edit-novel", label: "Edit Novel", icon: BookOpenIcon },
    { to: "project-requests", label: "Project Requests", icon: FolderOpenIcon },
    {
      to: "assignment-requests",
      label: "Assignment Requests",
      icon: FolderOpenIcon,
    },
    { to: "users", label: "User Page", icon: UserGroupIcon },
    { to: "user-issues", label: "User Issues", icon: ExclamationCircleIcon },
    { to: "admin-messages", label: "Admin Messages", icon: AcademicCapIcon },
    { to: "send-mail", label: "Send Mail", icon: EnvelopeIcon },
    { to: "welcome-email", label: "Welcome Email", icon: EnvelopeOpenIcon },
    { to: "team-exam", label: "Team Exam", icon: TrophyIcon },
    { to: "subjects", label: "Edit Subjects", icon: BookmarkSquareIcon },
  ];

  return (
    <div className="dashboard-shell">
      <nav
        className={`sidebar ${isCollapsed ? "collapsed" : ""} ${
          isMobileOpen ? "mobile-open" : ""
        }`}
      >
        <div className="sidebar-header">
          {(!isCollapsed || isMobileOpen) && (
            <h2 className="sidebar-title">MyQuest Admin</h2>
          )}
          <button onClick={toggleSidebar} className="sidebar-toggle desktop-only">
            {isCollapsed ? (
              <Bars3Icon className="h-6 w-6" />
            ) : (
              <XMarkIcon className="h-6 w-6" />
            )}
          </button>
          <button
            onClick={closeMobileSidebar}
            className="sidebar-toggle mobile-only"
            aria-label="Close menu"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <ul className="sidebar-nav">
          {navItems.map(({ to, label, icon: Icon }) => (
            <li key={to}>
              <NavLink
                to={to}
                onClick={closeMobileSidebar}
                className={({ isActive }) =>
                  `sidebar-link ${isActive ? "active" : ""} ${
                    isCollapsed && !isMobileOpen ? "collapsed" : ""
                  }`
                }
              >
                <Icon className="h-5 w-5" />
                {(!isCollapsed || isMobileOpen) && <span>{label}</span>}
              </NavLink>
            </li>
          ))}
        </ul>

        <div className="sidebar-footer">
          <button
            onClick={handleLogout}
            className={
              isCollapsed && !isMobileOpen ? "sidebar-toggle" : "sidebar-link"
            }
          >
            <ArrowLeftOnRectangleIcon className="h-5 w-5" />
            {(!isCollapsed || isMobileOpen) && <span>Logout</span>}
          </button>
        </div>
      </nav>

      {isMobileOpen && (
        <button
          type="button"
          className="sidebar-overlay mobile-visible"
          onClick={closeMobileSidebar}
          aria-label="Close menu"
        />
      )}

      <main className="main-panel">
        <button
          className="mobile-menu-btn"
          onClick={() => setIsMobileOpen(true)}
          aria-label="Open menu"
        >
          <Bars3Icon className="h-6 w-6" />
        </button>
        <Outlet />
      </main>
    </div>
  );
};

export default DashboardLayout;
